import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  FollowedChannel,
  FollowedChannelCreatedEnvelope,
  ListFollowedChannelsResponse,
  createFollowedChannel as universalCreateFollowedChannel,
  listFollowedChannels as universalListFollowedChannels,
} from './loyalty-socialmedia-v1-followed-channel-social-media.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/loyalty' };

export function createFollowedChannel(
  httpClient: HttpClient
): CreateFollowedChannelSignature {
  return (followedChannel: NonNullablePaths<FollowedChannel, `channel`, 2>) =>
    universalCreateFollowedChannel(
      followedChannel,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateFollowedChannelSignature {
  /**
   * Creates an entity for the specified account ID when the account follows a social media channel.
   *
   * Members can only follow enabled channels. A Wix user has to enable channels in the dashboard.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   * @param - Followed social media channel details.
   * @returns Followed social media channel details.
   */
  (followedChannel: NonNullablePaths<FollowedChannel, `channel`, 2>): Promise<
    NonNullablePaths<FollowedChannel, `_id` | `accountId` | `channel`, 2>
  >;
}

export function listFollowedChannels(
  httpClient: HttpClient
): ListFollowedChannelsSignature {
  return () =>
    universalListFollowedChannels(
      // @ts-ignore
      { httpClient }
    );
}

interface ListFollowedChannelsSignature {
  /**
   * Retrieves a list of social media channels followed by the account. The list is ordered by creation date.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   */
  (): Promise<
    NonNullablePaths<
      ListFollowedChannelsResponse,
      | `followedChannels`
      | `followedChannels.${number}._id`
      | `followedChannels.${number}.accountId`
      | `followedChannels.${number}.channel`,
      4
    >
  >;
}

export const onFollowedChannelCreated = EventDefinition(
  'wix.loyalty.socialmedia.v1.followed_channel_created',
  true,
  (event: FollowedChannelCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<FollowedChannelCreatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CreateFollowedChannelRequest,
  CreateFollowedChannelResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FollowedChannel,
  FollowedChannelCreatedEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListFollowedChannelsRequest,
  ListFollowedChannelsResponse,
  MessageEnvelope,
  RestoreInfo,
  Type,
  WebhookIdentityType,
} from './loyalty-socialmedia-v1-followed-channel-social-media.universal.js';
